// ============ REFERRAL PARTNERS (Brand Builder list) ============
// Contacts/companies who refer deals TO IndustryPro — a third concept next
// to incoming Referrals and GPP Partners. Every deal team keeps a "Brand
// Builder list" they add partners to; the two KPIs (added per month by
// owner, Referral Partner Meetings per month) read the referral_partners
// table + notes. Adding here round-trips to DealCloud (tags +
// BrandListAddedDate on the contact) via the write-back outbox, so the
// DC KPI views keep counting. Log a meeting from the partner's contact
// page with note subtype "Referral Partner Meeting".

const RP_STATUS_HUE = { active: 'green', removed: 'slate' };

const fmtRpDate = (iso) => iso
  ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
  : '—';

// Add-partner drawer: search existing contacts, pick owner + date, submit.
// A brand-new partner (the wealth advisor who just called) can be created
// in place via "+ New contact" — no detour to the People page.
const AddReferralPartnerDrawer = ({ users, activeUser, onClose, onAdded }) => {
  const [q, setQ] = React.useState('');
  const [results, setResults] = React.useState(null);
  const [picked, setPicked] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [ownerId, setOwnerId] = React.useState(activeUser?.id || '');
  const [added, setAdded] = React.useState(window.localISODate());
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    const s = q.trim();
    if (s.length < 3) { setResults(null); return; }
    const t = setTimeout(() => {
      window.AtlasApi.listContacts({ q: s, limit: 8 })
        .then((r) => setResults(r?.items || []))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const submit = async () => {
    if (!picked || busy) return;
    setBusy(true);
    try {
      await window.AtlasApi.createReferralPartner({
        contact_id: picked.id,
        owner_id: ownerId || null,
        brand_list_added_date: added || null,
      });
      pushToast({ type: 'success', title: 'Added to Brand Builder list' });
      onAdded();
      onClose();
    } catch (e) {
      pushToast({ type: 'error', title: 'Add failed', body: e.message });
      setBusy(false);
    }
  };

  return (
    <window.Drawer open onClose={onClose} title="Add referral partner" width={520}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Contact</div>
          {picked ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
              <span style={{ fontWeight: 600 }}>{picked.full_name}</span>
              <span style={{ color: 'var(--text-3)' }}>{picked.company_name || ''}</span>
              <Btn size="xs" variant="ghost" onClick={() => setPicked(null)}>change</Btn>
            </div>
          ) : (
            <>
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search people (3+ chars)…"
                style={{ width: '100%', padding: '6px 10px', fontSize: 12.5,
                         border: '1px solid var(--border)', borderRadius: 6, fontFamily: 'inherit' }} />
              {results && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 6, marginTop: 4,
                              maxHeight: 220, overflow: 'auto' }}>
                  {results.length === 0 && (
                    <div style={{ padding: 10, fontSize: 12, color: 'var(--text-3)' }}>No matches.</div>
                  )}
                  <div onClick={() => setCreating(true)}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = ''}
                    style={{ padding: '7px 10px', fontSize: 12.5, cursor: 'pointer', fontWeight: 500,
                             color: 'var(--accent-text)',
                             borderBottom: results.length ? '1px solid var(--border)' : 'none' }}>
                    + New contact “{q.trim()}”
                  </div>
                  {results.map((c) => (
                    <div key={c.id} onClick={() => setPicked(c)}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = ''}
                      style={{ padding: '7px 10px', fontSize: 12.5, cursor: 'pointer',
                               display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontWeight: 500 }}>{c.full_name}</span>
                      <span style={{ color: 'var(--text-3)', overflow: 'hidden',
                                     textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.company_name || ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Owner (deal team)</div>
            <Combobox value={ownerId} onChange={(e) => setOwnerId(e.target.value)}
              options={[{ value: '', label: '— no owner —' },
                        ...users.map((u) => ({ value: u.id, label: u.full_name }))]} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Added date</div>
            <input type="date" value={added} onChange={(e) => setAdded(e.target.value)}
              style={{ padding: '5px 8px', fontSize: 12, border: '1px solid var(--border)',
                       borderRadius: 6, fontFamily: 'inherit' }} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <Btn size="sm" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn size="sm" variant="accent" disabled={!picked || busy} onClick={submit}>
            {busy ? 'Adding…' : 'Add to list'}
          </Btn>
        </div>
      </div>
      {creating && ReactDOM.createPortal(
        <window.NewPersonDrawer
          initialName={q.trim()}
          initialType="professional"
          onClose={() => setCreating(false)}
          onCreated={(contact) => {
            setCreating(false);
            if (contact?.id) setPicked(contact);
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
          }}
        />, document.body)}
    </window.Drawer>
  );
};

// Owner filter opens on the signed-in user's own partners — each deal team
// works its own Brand Builder list, and Thomas was re-selecting his name on
// every visit. Flipping to "Any owner" (or back to yourself) sticks.
const RP_OWNER_SCOPE = window.makeOwnerScope('atlas:rp-owner-scope');

// ────────────────────────────────────────────────────────────────────────
// Standard grid stack (same as People/Companies/Engagements): ONE column
// catalog mapped by both the header and the body, per-user
// show/hide/reorder/resize via useColumnPrefs('referral-partners'),
// clickable-header sorting, and exports that mirror the visible layout.
// All rows are in hand (≤500 partners), so sorting is purely client-side.

const RP_SORT_EXTRACT = {
  partner:     (p) => (p.contact_name || '').toLowerCase() || null,
  firm:        (p) => (p.company_name || '').toLowerCase() || null,
  owner:       (p) => (p.owner_name || '').toLowerCase() || null,
  // Sort by the date the Added column actually shows: the DealCloud Brand
  // Builder date, falling back to when Atlas first saw the row.
  added:       (p) => p.brand_list_added_date || p.created_at || null,
  lastMeeting: (p) => p.last_meeting_date || null,
  status:      (p) => p.status || null,
  notes:       (p) => (p.notes || '').toLowerCase() || null,
  source:      (p) => (p.source || '').toLowerCase() || null,
  firstSeen:   (p) => p.created_at || null,
  lastSynced:  (p) => p.last_synced_at || null,
};

// Simple one-line cell — the catalog's render fns share it so every column
// sizes to col.w and rules its right divider the same way.
const rpCell = (col, content, opts = {}) => (
  <div key={col.key}
    onClick={opts.stop ? (e) => e.stopPropagation() : undefined}
    title={opts.title}
    style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center',
             minWidth: 0, fontSize: 12, color: opts.color || 'var(--text-2)',
             borderRight: col.last ? 'none' : '1px solid var(--border)',
             overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
    {content}
  </div>
);

// There is deliberately no Updated column in this catalog: the hourly Brand
// Builder sweep rewrites every row, so referral_partners.updated_at is sync
// churn (271 of 273 rows carry today's date). Owner, Added and Notes are
// inline-editable — the PATCH enqueues a DealCloud write-back, same as the
// add flow.
const RP_COLUMNS = [
  {
    key: 'partner', label: 'Partner', fixed: true, extra: 40,
    accessor: (p) => p.contact_name,
    xget: (p) => p.contact_name || '',
    // Email rides along in the export whenever Partner is visible (always —
    // it's the fixed identity column), preserving the old file shape.
    xextra: [{ key: 'contact_email', label: 'Email', get: (p) => p.contact_email || '' }],
    render: (p, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '6px 10px', minWidth: 0,
                                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                  borderRight: col.last ? 'none' : '1px solid var(--border)' }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.contact_name}
        </div>
        {p.contact_email && (
          <div style={{ fontSize: 11, color: 'var(--text-3)', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {p.contact_email}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'firm', label: 'Firm',
    accessor: (p) => p.company_name || '',
    render: (p, ctx, col) => rpCell(col, p.company_name || '—',
      { color: p.company_name ? 'var(--text-2)' : 'var(--text-4)' }),
  },
  {
    key: 'owner', label: 'Owner', extra: 24,
    accessor: (p) => p.owner_name || '(no owner)',
    xget: (p) => p.owner_name || '',
    render: (p, ctx, col) => rpCell(col, (
      <window.EditableCell type="user" value={p.owner_id} users={ctx.users}
        fallbackLabel={p.owner_name}
        placeholder="(no owner)"
        onSave={(owner_id) => ctx.onSave(p.id, { owner_id })} />
    ), { stop: true }),
  },
  {
    key: 'added', label: 'Added', extra: 16,
    desc: 'On the DealCloud Brand Builder list since. Editable.',
    accessor: (p) => fmtRpDate(p.brand_list_added_date || p.created_at),
    xget: (p) => p.brand_list_added_date || '',
    render: (p, ctx, col) => rpCell(col, (
      <window.EditableCell type="date"
        value={p.brand_list_added_date ? p.brand_list_added_date.slice(0, 10) : null}
        displayAs={p.brand_list_added_date ? (
          <span title={`On the Brand Builder list since ${fmtRpDate(p.brand_list_added_date)} (DealCloud)`}>
            {fmtRpDate(p.brand_list_added_date)}
          </span>
        ) : (
          <span style={{ color: 'var(--text-4)', fontStyle: 'italic' }}
            title={p.created_at
              ? `No Brand Builder date in DealCloud — first seen in Atlas ${fmtRpDate(p.created_at)}`
              : 'No Brand Builder date in DealCloud'}>
            {p.created_at ? fmtRpDate(p.created_at) : '—'}
          </span>
        )}
        onSave={(brand_list_added_date) => ctx.onSave(p.id, { brand_list_added_date })} />
    ), { stop: true }),
  },
  {
    key: 'lastMeeting', label: 'Last meeting',
    desc: 'Most recent "Referral Partner Meeting" note on the contact — Fireflies/Outlook-inferred meetings count.',
    accessor: (p) => fmtRpDate(p.last_meeting_date),
    xget: (p) => p.last_meeting_date || '',
    render: (p, ctx, col) => rpCell(col, fmtRpDate(p.last_meeting_date),
      { color: p.last_meeting_date ? 'var(--text-2)' : 'var(--text-4)' }),
  },
  {
    key: 'status', label: 'Status', extra: 12,
    accessor: (p) => p.status || '',
    render: (p, ctx, col) => rpCell(col, (
      <Pill hue={RP_STATUS_HUE[p.status] || 'slate'} sz="sm">{p.status}</Pill>
    )),
  },

  // ── Off by default, one click away in the Columns menu.
  {
    key: 'notes', label: 'Notes', def: false, group: 'Details',
    desc: 'Membership notes on the Brand Builder row. Editable.',
    accessor: (p) => p.notes || '',
    render: (p, ctx, col) => rpCell(col, (
      <window.EditableCell type="text" value={p.notes || ''}
        onSave={(notes) => ctx.onSave(p.id, { notes })} />
    ), { stop: true }),
  },
  {
    key: 'source', label: 'Source', def: false, group: 'Sync',
    desc: 'How the row got here.',
    accessor: (p) => p.source || '',
    render: (p, ctx, col) => rpCell(col, p.source || '—',
      { color: p.source ? 'var(--text-2)' : 'var(--text-4)' }),
  },
  {
    key: 'firstSeen', label: 'First seen', def: false, group: 'Sync',
    desc: 'When Atlas first saw the row — the fallback the Added column shows when DealCloud has no Brand Builder date.',
    accessor: (p) => fmtRpDate(p.created_at),
    xget: (p) => p.created_at || '',
    render: (p, ctx, col) => rpCell(col, fmtRpDate(p.created_at),
      { color: p.created_at ? 'var(--text-2)' : 'var(--text-4)' }),
  },
  {
    key: 'lastSynced', label: 'Last synced', def: false, group: 'Sync',
    accessor: (p) => fmtRpDate(p.last_synced_at),
    xget: (p) => p.last_synced_at || '',
    render: (p, ctx, col) => rpCell(col, fmtRpDate(p.last_synced_at),
      { color: p.last_synced_at ? 'var(--text-2)' : 'var(--text-4)' }),
  },
];

// Identifier columns appended to every export regardless of layout, so a
// file can always be joined back to Atlas.
const RP_EXPORT_ALWAYS = [
  { key: 'id',         label: 'Atlas id',   get: (p) => p.id },
  { key: 'contact_id', label: 'Contact id', get: (p) => p.contact_id },
];

// Memoized row — an inline cell edit swaps ONE row object in state, so only
// that row re-renders (same pattern as PersonRow). The trailing remove cell
// stays outside the catalog: it's a row action, not a column.
const RpRow = React.memo(({ p, cols, cellCtx, onOpen, onRemove }) => (
  <div
    onClick={() => onOpen(p.contact_id)}
    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
    onMouseLeave={(e) => e.currentTarget.style.background = 'var(--surface)'}
    style={{ display: 'flex', minHeight: 44, alignItems: 'stretch',
             borderBottom: '1px solid var(--border)', background: 'var(--surface)',
             cursor: 'pointer' }}>
    {cols.map((col) => col.render(p, cellCtx, col))}
    <div onClick={(e) => e.stopPropagation()}
      style={{ width: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {p.status === 'active' && (
        <Btn size="xs" variant="ghost" title="Remove from list"
             onClick={() => onRemove(p)} style={{ color: 'var(--rose-fg)' }}>✕</Btn>
      )}
    </div>
  </div>
));

const ReferralPartnersView = ({ setContactId, activeUser }) => {
  const [partners, setPartners] = React.useState(null);
  const [q, setQ] = React.useState('');
  const [debouncedQ, setDebouncedQ] = React.useState('');
  const [users, setUsers] = React.useState([]);
  const [adding, setAdding] = React.useState(false);
  const [filters, setFilters] = React.useState(() => ({
    owner_id: RP_OWNER_SCOPE.get(activeUser?.role) === 'mine' ? (activeUser?.id || '') : '',
    status: 'active',
  }));
  // activeUser can arrive after mount on a cold boot. Only the scope-derived
  // default may move the filter — once Owner (or Clear) has been touched,
  // whatever is on screen wins.
  const ownerTouched = React.useRef(false);
  React.useEffect(() => {
    if (ownerTouched.current || !activeUser?.id) return;
    setFilters((f) => {
      const want = RP_OWNER_SCOPE.get(activeUser.role) === 'mine' ? activeUser.id : '';
      return f.owner_id === want ? f : { ...f, owner_id: want };
    });
  }, [activeUser?.id, activeUser?.role]);
  const changeOwner = (v) => {
    ownerTouched.current = true;
    if (v === '') RP_OWNER_SCOPE.set('any');
    else if (activeUser?.id && v === activeUser.id) RP_OWNER_SCOPE.set('mine');
    setFilters((f) => ({ ...f, owner_id: v }));
  };

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  React.useEffect(() => {
    window.AtlasApi.listTeamMembers()
      .then((m) => setUsers(Array.isArray(m) ? m.filter((u) => u.active !== false) : []))
      .catch(() => setUsers([]));
  }, []);

  const reload = React.useCallback(() => {
    setPartners(null);
    const params = { limit: 500, status: filters.status || 'active' };
    if (debouncedQ) params.q = debouncedQ;
    if (filters.owner_id) params.owner_id = filters.owner_id;
    window.AtlasApi.listReferralPartners(params)
      .then((r) => setPartners((r && r.items) || []))
      .catch(() => setPartners([]));
  }, [debouncedQ, filters]);
  React.useEffect(() => { reload(); }, [reload]);

  // Column sort — client-side, every row is in hand. Default (no sortKey)
  // keeps the server order: Brand Builder date desc, nulls last.
  const [sortKey, setSortKey] = React.useState(null);
  const [sortDir, setSortDir] = React.useState('asc');
  const toggleSort = React.useMemo(
    () => window.makeToggleSort(setSortKey, setSortDir), []
  );

  // Inline cell editing — PATCH the membership row, merge the server's
  // response back in (owner_name resolved server-side). The PATCH enqueues a
  // DealCloud write-back, so edits keep the DC KPI views counting. Only the
  // edited row object changes identity, so the memoized RpRow re-renders
  // alone. last_meeting_date is preserved from the row in hand: the PATCH
  // serializer doesn't recompute it.
  const saveRp = React.useCallback(async (id, patch) => {
    const updated = await window.AtlasApi.updateReferralPartner(id, patch);
    setPartners((prev) => (prev || []).map((x) =>
      (x.id === id ? { ...x, ...updated, last_meeting_date: x.last_meeting_date } : x)));
  }, []);

  const rows = partners || [];
  // Per-user column layout (105).
  const columns = window.useColumnPrefs('referral-partners', RP_COLUMNS, {
    rows, fitMax: 360,
  });
  // One object so the memoized row can't capture a stale per-cell closure.
  const cellCtx = React.useMemo(() => ({ onSave: saveRp, users }), [saveRp, users]);

  const sorted = React.useMemo(() => (
    sortKey
      ? [...rows].sort(window.makeComparator(sortKey, sortDir, RP_SORT_EXTRACT))
      : rows
  ), [rows, sortKey, sortDir]);

  const kpis = React.useMemo(() => {
    const thisMonth = window.localISODate().slice(0, 7);
    return {
      total: rows.length,
      added_this_month: rows.filter((p) => (p.brand_list_added_date || '').slice(0, 7) === thisMonth).length,
      with_meeting: rows.filter((p) => p.last_meeting_date).length,
      unowned: rows.filter((p) => !p.owner_id).length,
    };
  }, [rows]);

  const removePartner = (p) => {
    showConfirm({
      title: 'Remove from Brand Builder list?',
      body: 'The contact stays; the row is soft-removed. DC tags are NOT removed automatically.',
      onConfirm: async () => {
        try {
          await window.AtlasApi.removeReferralPartner(p.id);
          pushToast({ type: 'success', title: 'Removed from the list' });
          reload();
        } catch (e) {
          pushToast({ type: 'error', title: 'Remove failed', body: e.message });
        }
      },
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Referral Partners']}
        title="Referral Partners"
        actions={(
          <>
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or firm…"
              style={{ width: 240, padding: '4px 10px', fontSize: 12,
                       border: '1px solid var(--border)', borderRadius: 6, height: 28, fontFamily: 'inherit' }} />
            {/* Rows are all in hand (≤500 partners) — export what the
                current search + filters show, mirroring the grid: the
                columns you can see, in your order and sort. */}
            <window.ExportMenu
              disabled={partners === null || !rows.length}
              viewCount={rows.length}
              onExport={(format) => {
                const xcols = window.exportColumnsFor(columns.cols, RP_EXPORT_ALWAYS);
                const records = window.exportRecords(xcols, sorted);
                const name = `referral-partners-${window.localISODate()}`;
                if (format === 'csv') window.exportCsv(`${name}.csv`, xcols, records);
                else window.exportJson(`${name}.json`, records);
              }}
            />
            <window.ColumnsMenu
              catalog={RP_COLUMNS}
              cols={columns.cols}
              toggle={columns.toggle}
              move={columns.move}
              reset={columns.reset}
            />
            <Btn size="sm" variant="accent" icon={<Icon.Plus size={11} />} onClick={() => setAdding(true)}>
              Add partner
            </Btn>
          </>
        )}
      />

      <div style={{ flex: 1, overflow: 'auto', background: 'var(--surface-2)', padding: 16 }}>
        {/* Filter bar */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 8, padding: 10, marginBottom: 14,
                      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Owner:</span>
            <Combobox value={filters.owner_id}
              onChange={(e) => changeOwner(e.target.value)}
              options={[{ value: '', label: 'Any owner' },
                        ...users.map((u) => ({ value: u.id, label: u.full_name }))]}
              style={{ minWidth: 180 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Status:</span>
            <Combobox value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              options={[
                { value: 'active',  label: 'Active' },
                { value: 'removed', label: 'Removed' },
                { value: 'all',     label: 'All' },
              ]}
              style={{ minWidth: 120 }} />
          </div>
          <div style={{ flex: 1 }} />
          <Btn size="xs" variant="ghost"
            onClick={() => {
              // Clear is a one-visit "show me everything" — it doesn't
              // change the remembered owner default.
              ownerTouched.current = true;
              setFilters({ owner_id: '', status: 'active' }); setQ('');
            }}>
            Clear
          </Btn>
        </div>

        {/* KPI strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
          {[
            { label: 'Showing',          value: kpis.total },
            { label: 'Added this month', value: kpis.added_this_month },
            { label: 'Have met',         value: kpis.with_meeting },
            { label: 'No owner',         value: kpis.unowned },
          ].map((k) => (
            <div key={k.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                                        borderRadius: 6, padding: '8px 12px' }}>
              <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                            letterSpacing: '0.05em', fontWeight: 500 }}>{k.label}</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>
                {partners === null ? '…' : k.value.toLocaleString()}
              </div>
            </div>
          ))}
        </div>

        {/* Table — header and body both map over columns.cols, so they can't
            drift apart. overflowY stays 'hidden' (not 'auto'): WindowedRows
            walks up to the nearest overflow-y auto/scroll ancestor for its
            scroll events, and that must resolve to the page scroller above,
            not this card (which never scrolls vertically — it grows). */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 8, overflowX: 'auto', overflowY: 'hidden' }}>
          {partners === null && (
            <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
          )}
          {partners && rows.length === 0 && (
            <div style={{ padding: 32, fontSize: 12.5, color: 'var(--text-3)' }}>
              No referral partners match.
              {filters.owner_id
                ? ' The list is filtered to one owner — set Owner to “Any owner” to see everyone\'s partners, or add one with the button above.'
                : ' Add one with the button above.'}
            </div>
          )}
          {partners && rows.length > 0 && (
            <div style={{ minWidth: 'max-content' }}>
              <div style={{ display: 'flex', height: 30, background: 'var(--surface-2)',
                            fontSize: 11, color: 'var(--text-3)', fontWeight: 500,
                            textTransform: 'uppercase', letterSpacing: '0.04em',
                            borderBottom: '1px solid var(--border)' }}>
                {columns.cols.map((c) => (
                  <window.SortableHeader key={c.key} col={c}
                    sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}
                    sortable={Boolean(RP_SORT_EXTRACT[c.key])}
                    onResizeStart={columns.startResize(c.key)}
                  />
                ))}
                {/* Spacer over the row-action (remove) cell. */}
                <div style={{ width: 40 }} />
              </div>
              <window.WindowedRows rows={sorted} renderRow={(p) => (
                <RpRow key={p.id} p={p} cols={columns.cols} cellCtx={cellCtx}
                  onOpen={setContactId} onRemove={removePartner} />
              )} />
            </div>
          )}
        </div>
      </div>

      {adding && (
        <AddReferralPartnerDrawer
          users={users}
          activeUser={activeUser}
          onClose={() => setAdding(false)}
          onAdded={reload}
        />
      )}
    </div>
  );
};

window.ReferralPartnersView = ReferralPartnersView;
